import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Search, X, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { api } from '../../lib/api';
import { useSEO } from '../../lib/seo';
import { useAdminAuth } from './AdminGate';

const STATUS_STYLES = {
  new: 'border-brand-lineBlue bg-brand-ice text-brand-skyDeep',
  contacted: 'border-amber-200 bg-amber-50 text-amber-700',
  closed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const STATUS_LABELS = { new: 'New', contacted: 'Contacted', closed: 'Closed' };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const AdminDashboard = () => {
  useSEO({ title: 'Admin Dashboard — CapableBee' });
  const navigate = useNavigate();
  const { email: me } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  const load = useCallback(
    async (p = 1) => {
      setLoading(true);
      setError('');
      try {
        const r = await api.get('/admin/submissions', { params: { search, status, sort, page: p } });
        setItems(r.data.items);
        setTotal(r.data.total);
        setPage(r.data.page);
        setPages(r.data.pages);
      } catch (err) {
        if (err.response?.status === 401) navigate('/admin/login');
        else setError('Failed to load submissions.');
      } finally {
        setLoading(false);
      }
    },
    [search, status, sort, navigate]
  );

  useEffect(() => {
    load(1);
  }, [status, sort, load]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, load]);

  const changeStatus = async (id, next) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status: next });
      setItems((its) => its.map((it) => (it.id === id ? { ...it, status: next } : it)));
      setSelected((s) => (s && s.id === id ? { ...s, status: next } : s));
    } catch {
      setError('Could not update status.');
    }
  };

  const logout = async () => {
    try {
      await api.post('/admin/logout');
    } catch {
      /* still navigate */
    }
    navigate('/admin/login');
  };

  return (
    <main className="min-h-screen bg-brand-iceSoft" data-testid="admin-dashboard">
      <header className="border-b border-brand-line bg-white">
        <div className="container-x flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.png" alt="CapableBee" className="h-8 w-auto" />
            <span className="rounded-full bg-brand-ice px-3 py-1 text-xs font-bold text-brand-skyDeep">
              Submissions Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm font-semibold text-slate-500 sm:block" data-testid="admin-email">
              {me}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-bold text-brand-navy transition-all hover:-translate-y-0.5 hover:shadow-card"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, subject, message…"
              className="w-full rounded-full border border-brand-line bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/25"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-semibold text-brand-navy outline-none"
            data-testid="admin-status-filter"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-semibold text-brand-navy outline-none"
            data-testid="admin-sort-select"
            aria-label="Sort order"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <span className="ml-auto text-sm font-semibold text-slate-500" data-testid="admin-total">
            {total} submission{total === 1 ? '' : 's'}
          </span>
        </div>

        {error && (
          <p role="alert" className="mt-4 text-sm font-semibold text-red-500" data-testid="admin-error">
            {error}
          </p>
        )}

        <div className="mt-6 overflow-hidden rounded-3xl border border-brand-line bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-table">
              <thead>
                <tr className="border-b border-brand-line bg-brand-iceSoft text-xs uppercase tracking-widest text-slate-400">
                  <th className="px-5 py-4 font-bold">Name</th>
                  <th className="px-5 py-4 font-bold">Email</th>
                  <th className="px-5 py-4 font-bold">Phone</th>
                  <th className="px-5 py-4 font-bold">Subject</th>
                  <th className="px-5 py-4 font-bold">Date</th>
                  <th className="px-5 py-4 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-14 text-center text-slate-400">
                      Loading…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-14 text-center text-slate-400">
                      <Inbox className="mx-auto mb-3 h-8 w-8" />
                      No submissions found.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr
                      key={it.id}
                      onClick={() => setSelected(it)}
                      className="cursor-pointer border-b border-slate-50 transition-colors last:border-0 hover:bg-brand-iceSoft/60"
                      data-testid={`admin-row-${it.id}`}
                    >
                      <td className="px-5 py-4 font-bold text-brand-navy">{it.first_name}</td>
                      <td className="px-5 py-4 text-brand-body">{it.email}</td>
                      <td className="px-5 py-4 text-brand-body">{it.phone || '—'}</td>
                      <td className="max-w-[200px] truncate px-5 py-4 text-brand-body">{it.subject || '—'}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(it.created_at)}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-full border px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[it.status]}`}
                        >
                          {STATUS_LABELS[it.status]}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-brand-line px-5 py-4">
            <p className="text-sm text-slate-500">
              Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1 || loading}
                data-testid="admin-prev-page"
                className="inline-flex items-center gap-1 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-bold text-brand-navy disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <button
                onClick={() => load(page + 1)}
                disabled={page >= pages || loading}
                data-testid="admin-next-page"
                className="inline-flex items-center gap-1 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-bold text-brand-navy disabled:opacity-40"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSelected(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Submission details"
          data-testid="admin-detail-modal"
        >
          <div className="w-full max-w-lg rounded-3xl border border-brand-line bg-white p-7 shadow-lift">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-brand-navy">{selected.first_name}</h2>
                <p className="mt-0.5 text-sm text-slate-500">{fmtDate(selected.created_at)}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                aria-label="Close details"
                data-testid="admin-modal-close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-line text-brand-navy transition-colors hover:bg-brand-ice"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-bold text-slate-400">Email</dt>
                <dd className="break-all font-semibold text-brand-navy">{selected.email}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-bold text-slate-400">Phone</dt>
                <dd className="font-semibold text-brand-navy">{selected.phone || '—'}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-bold text-slate-400">Company</dt>
                <dd className="font-semibold text-brand-navy">{selected.company || '—'}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-bold text-slate-400">Subject</dt>
                <dd className="font-semibold text-brand-navy">{selected.subject || '—'}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 font-bold text-slate-400">Message</dt>
                <dd className="whitespace-pre-wrap leading-relaxed text-brand-body">{selected.message}</dd>
              </div>
            </dl>

            <div className="mt-7 border-t border-slate-100 pt-5">
              <p className="mb-2.5 text-xs font-bold uppercase tracking-widest text-slate-400">Change status</p>
              <div className="flex gap-2" data-testid="admin-status-buttons">
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => changeStatus(selected.id, value)}
                    data-testid={`admin-status-${value}`}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                      selected.status === value
                        ? STATUS_STYLES[value]
                        : 'border-brand-line bg-white text-slate-500 hover:border-brand-lineBlue'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
